import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useT } from '../i18n';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useRegistrations } from '../context/RegistrationsContext';
import { CalendarPrompt } from '../components/CalendarUtils';

// Map from backend status values → display meta
const STATUS_META = {
  participating:     { icon: '✓', label: 'Angemeldet',      bg: 'rgba(var(--brand-primary-rgb),0.15)',   border: 'rgba(var(--brand-primary-rgb),0.5)',   color: 'var(--primary)' },
  assigned:          { icon: '✓', label: 'Angemeldet',      bg: 'rgba(var(--brand-primary-rgb),0.15)',   border: 'rgba(var(--brand-primary-rgb),0.5)',   color: 'var(--primary)' },
  not_participating: { icon: '⊘', label: 'Abgemeldet',      bg: 'rgba(147,0,10,0.15)',    border: 'rgba(255,180,171,0.4)', color: 'var(--error)' },
  ineligible:        { icon: '⊘', label: 'Nicht eligible',  bg: 'rgba(147,0,10,0.15)',    border: 'rgba(255,180,171,0.4)', color: 'var(--error)' },
  none:              { icon: '×', label: 'Keine Anmeldung',  bg: 'transparent',            border: 'rgba(59,73,75,0.5)',    color: 'var(--outline)' },
};

// Cycle order for driver clicks: participating → not_participating → null → participating
const DRIVER_STATUS_CYCLE = ['participating', 'not_participating', null];

/* ── Desktop status cell (unchanged) ── */
function StatusCell({ status, onClick, canEdit }) {
  const meta = STATUS_META[status] || STATUS_META.none;
  return (
    <div
      onClick={canEdit ? onClick : undefined}
      title={meta.label}
      style={{
        width: 44, height: 44,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: meta.bg, border: `1px solid ${meta.border}`,
        color: meta.color, fontFamily: "'JetBrains Mono', monospace",
        fontSize: 14, fontWeight: 700,
        cursor: canEdit ? 'pointer' : 'default',
        transition: 'opacity 0.15s', userSelect: 'none',
      }}
      onMouseEnter={e => { if (canEdit) e.currentTarget.style.opacity = '0.7'; }}
      onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
    >
      {meta.icon}
    </div>
  );
}

/* ── Mobile: single driver row inside an accordion ── */
function MobileDriverRow({ driver, status, canEdit, onToggle, saving, isMe }) {
  const meta = STATUS_META[status] || STATUS_META.none;
  const initials = (driver.name || '?')
    .split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 0',
      opacity: status === 'not_participating' ? 0.6 : 1,
    }}>
      {/* Left: avatar + name */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {driver.photo_url ? (
          <img src={driver.photo_url} alt={initials} style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: `1px solid ${isMe ? 'rgba(var(--brand-primary-rgb),0.4)' : 'rgba(59,73,75,0.3)'}` }} />
        ) : (
          <div style={{
            width: 34, height: 34, flexShrink: 0,
            background: isMe ? 'rgba(var(--brand-primary-rgb),0.12)' : 'rgba(51,53,57,0.8)',
            border: `1px solid ${isMe ? 'rgba(var(--brand-primary-rgb),0.4)' : 'rgba(59,73,75,0.3)'}`,
            borderRadius: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, color: isMe ? 'var(--brand-primary)' : 'var(--on-surface-variant)' }}>
              {initials}
            </span>
          </div>
        )}
        <div>
          <span style={{ fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 600, color: 'var(--on-surface)' }}>
            {(driver.name || '?').split(' ').map((p, i) => i === 0 ? p[0] + '.' : p).join(' ')}
          </span>
          {isMe && (
            <span style={{ marginLeft: 6, fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, color: 'var(--brand-primary)', background: 'rgba(var(--brand-primary-rgb),0.1)', border: '1px solid rgba(var(--brand-primary-rgb),0.25)', padding: '1px 5px' }}>ICH</span>
          )}
        </div>
      </div>

      {/* Right: status + optional toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontStyle: 'italic', fontWeight: 700, color: meta.color, letterSpacing: '0.06em' }}>
          {meta.label.toUpperCase()}
        </span>

        {canEdit ? (
          /* Clickable toggle button */
          <button
            onClick={onToggle}
            disabled={saving}
            style={{
              width: 28, height: 28, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: meta.bg,
              border: `1px solid ${meta.border}`,
              color: meta.color,
              fontFamily: "'JetBrains Mono', monospace", fontSize: 14, fontWeight: 700,
              cursor: saving ? 'wait' : 'pointer',
              transition: 'all 0.15s', userSelect: 'none',
            }}
          >
            {saving ? '…' : meta.icon}
          </button>
        ) : (
          /* Read-only status icon */
          <div style={{
            width: 28, height: 28, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: meta.bg, border: `1px solid ${meta.border}`,
            color: meta.color, fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12, fontWeight: 700,
          }}>
            {meta.icon}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Mobile: one event accordion card ── */
function MobileEventCard({ ev, drivers, getStatus, handleCellClick, isAdmin, isDriver, myDriverId, saving, fmtDate, categoryFilter, onNavigate }) {
  const [open, setOpen] = useState(false);

  const filteredDrivers = categoryFilter === 'ALL'
    ? drivers
    : drivers.filter(d => (d.category || 'Elite') === categoryFilter);

  // Count assigned
  const assignedCount = filteredDrivers.filter(d => {
    const s = getStatus(ev.id, d.id);
    return s === 'participating' || s === 'assigned';
  }).length;

  const isCancelled = !!ev.is_cancelled;

  return (
    <div style={{ background: isCancelled ? 'rgba(28,4,4,0.8)' : ev.is_mandatory ? 'rgba(28,18,4,0.7)' : 'rgba(30,32,36,0.6)', backdropFilter: 'blur(20px)', border: `1px solid ${isCancelled ? 'rgba(220,50,50,0.35)' : ev.is_mandatory ? 'rgba(255,160,50,0.3)' : 'rgba(var(--brand-primary-rgb),0.12)'}`, marginBottom: 12 }}>
      {/* Summary row — always visible, click anywhere → toggle */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', userSelect: 'none', cursor: 'pointer' }}
      >
        {/* Left: event info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: (ev.is_mandatory || isCancelled) ? 2 : 0 }}>
            {isCancelled && (
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', background: '#dc2626', color: '#fff', padding: '1px 5px', flexShrink: 0 }}>✕ ABGESAGT</span>
            )}
            {ev.is_mandatory && !isCancelled && (
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', background: '#ff9a1f', color: '#1a0800', padding: '1px 5px', flexShrink: 0 }}>PFLICHT</span>
            )}
          </div>
          <div style={{ fontFamily: "var(--font-display)", fontSize: 17, fontWeight: 700, color: isCancelled ? 'rgba(220,100,100,0.7)' : 'var(--on-surface)', letterSpacing: '0.01em', lineHeight: 1.2, textDecoration: isCancelled ? 'line-through' : 'none' }}>
            {ev.name}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5 }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: ev.is_mandatory ? '#ff9a1f' : 'var(--brand-primary)', background: ev.is_mandatory ? 'rgba(255,140,0,0.1)' : 'rgba(var(--brand-primary-rgb),0.1)', border: `1px solid ${ev.is_mandatory ? 'rgba(255,160,50,0.3)' : 'rgba(var(--brand-primary-rgb),0.2)'}`, padding: '2px 7px' }}>
              {assignedCount}/{filteredDrivers.length} ANGEMELDET
            </span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontStyle: 'italic', color: 'var(--outline)' }}>
              {fmtDate(ev.date)}
            </span>
          </div>
        </div>
        {/* Right: detail button + chevron */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 10, flexShrink: 0 }}>
          {/* Detail button — stops propagation so it doesn't also toggle */}
          <button
            onClick={e => { e.stopPropagation(); onNavigate(ev.id); }}
            style={{
              fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700,
              padding: '5px 9px', border: '1px solid rgba(var(--brand-primary-rgb),0.3)',
              background: 'rgba(var(--brand-primary-rgb),0.08)', color: 'var(--brand-primary)',
              cursor: 'pointer', letterSpacing: '0.06em', lineHeight: 1,
            }}
            title="Event Details"
          >
            →
          </button>
          <span style={{ color: 'var(--brand-primary)', fontSize: 20, fontWeight: 300, transition: 'transform 0.25s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>
            ⌄
          </span>
        </div>
      </div>

      {/* Expanded rider list */}
      {open && (
        <div style={{ borderTop: '1px solid rgba(var(--brand-primary-rgb),0.1)', padding: '4px 16px 12px' }}>
          <div style={{ borderTop: '1px solid rgba(59,73,75,0.15)', marginTop: 4 }}>
            {filteredDrivers.map((driver, idx) => {
              const status = getStatus(ev.id, driver.id);
              const isMe = isDriver && driver.id === myDriverId;
              const canEdit = isMe; // Only drivers can edit their own row

              const onToggle = () => handleCellClick(ev.id, driver.id);

              return (
                <div key={driver.id} style={{ borderBottom: idx < filteredDrivers.length - 1 ? '1px solid rgba(59,73,75,0.12)' : 'none' }}>
                  <MobileDriverRow
                    driver={driver}
                    status={status}
                    canEdit={canEdit}
                    onToggle={onToggle}
                    saving={saving}
                    isMe={isMe}
                  />
                </div>
              );
            })}
          </div>

          {/* Hint */}
          {isDriver && (
            <div style={{ marginTop: 10, fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: 'rgba(132,148,149,0.6)', letterSpacing: '0.08em' }}>
              ↕ KLICKEN UM DICH AN-/ABZUMELDEN
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Mobile: flat event row for "Ich" view ── */
function MobileMyEventRow({ ev, myStatus, onToggle, onNavigate, saving, fmtDate }) {
  const meta = STATUS_META[myStatus] || STATUS_META.none;
  const isCancelled = !!ev.is_cancelled;
  const isNone = !myStatus;

  return (
    <div style={{
      background: isCancelled ? 'rgba(28,4,4,0.8)' : ev.is_mandatory ? 'rgba(28,18,4,0.7)' : 'rgba(30,32,36,0.6)',
      backdropFilter: 'blur(20px)',
      border: `1px solid ${isCancelled ? 'rgba(220,50,50,0.35)' : ev.is_mandatory ? 'rgba(255,160,50,0.3)' : 'rgba(var(--brand-primary-rgb),0.12)'}`,
      marginBottom: 10,
      display: 'flex', alignItems: 'center', padding: '14px 16px', gap: 12,
    }}>
      {/* Left: event info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {(isCancelled || ev.is_mandatory) && (
          <div style={{ display: 'flex', gap: 5, marginBottom: 4 }}>
            {isCancelled && (
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', background: '#dc2626', color: '#fff', padding: '1px 5px' }}>✕ ABGESAGT</span>
            )}
            {ev.is_mandatory && !isCancelled && (
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', background: '#ff9a1f', color: '#1a0800', padding: '1px 5px' }}>PFLICHT</span>
            )}
          </div>
        )}
        <div style={{
          fontFamily: "var(--font-display)",
          fontSize: 15, fontWeight: 700,
          color: isCancelled ? 'rgba(220,100,100,0.7)' : 'var(--on-surface)',
          letterSpacing: '0.01em', lineHeight: 1.2,
          textDecoration: isCancelled ? 'line-through' : 'none',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {ev.name}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5 }}>
          <span style={{
            fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700,
            color: meta.color, letterSpacing: '0.06em',
            background: meta.bg, border: `1px solid ${meta.border}`,
            padding: '2px 7px',
          }}>
            {isNone ? 'OFFEN' : meta.label.toUpperCase()}
          </span>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontStyle: 'italic', color: 'var(--outline)' }}>
            {fmtDate(ev.date)}{ev.location ? ` · ${ev.location.toUpperCase()}` : ''}
          </span>
        </div>
      </div>

      {/* Right: toggle + detail button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {!isCancelled && (
          <button
            onClick={onToggle}
            disabled={saving}
            title={isNone ? 'Anmelden' : meta.label}
            style={{
              width: 36, height: 36, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: meta.bg,
              border: `1px solid ${meta.border}`,
              color: meta.color,
              fontFamily: "'JetBrains Mono', monospace", fontSize: 16, fontWeight: 700,
              cursor: saving ? 'wait' : 'pointer',
              transition: 'all 0.15s', userSelect: 'none',
            }}
          >
            {saving ? '…' : isNone ? '+' : meta.icon}
          </button>
        )}
        <button
          onClick={() => onNavigate(ev.id)}
          style={{
            fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700,
            width: 36, height: 36, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid rgba(var(--brand-primary-rgb),0.3)',
            background: 'rgba(var(--brand-primary-rgb),0.08)', color: 'var(--brand-primary)',
            cursor: 'pointer',
          }}
        >
          →
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════ */
function RaceMatrix({ token, role, isCoAdmin = false, driverId: myDriverId }) {
  const t = useT();
  const isAdmin = role === 'admin';
  const isAdminOrCoAdmin = isAdmin || isCoAdmin;
  const isDriver = role === 'driver';
  const { registrations, setStatus, refresh: refreshRegs } = useRegistrations();
  const navigate = useNavigate();

  const [events, setEvents]   = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [saving, setSaving]   = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [activeTab, setActiveTab] = useState('upcoming');
  const [displayCount, setDisplayCount] = useState(20);
  const sentinelRef = useRef(null);
  const [calPromptEvent, setCalPromptEvent] = useState(null); // event object to show CalendarPrompt for
  const [mobileView, setMobileView] = useState('ich'); // 'ich' | 'team'

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [evRes, drRes] = await Promise.all([
        axios.get('/api/events', { headers }),
        axios.get('/api/drivers', { headers }),
      ]);
      setEvents((evRes.data || []).sort((a, b) => new Date(a.date) - new Date(b.date)));
      // Exclude owner row when not racing (has _isOwner=true and is_racing=false)
      const allDrivers = (drRes.data || []).filter(d => !d._isOwner || d.is_racing);
      setDrivers(allDrivers.sort((a, b) => a.name.localeCompare(b.name)));
      await refreshRegs();
    } catch (err) {
      setError('Daten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Reset displayCount when tab changes
  useEffect(() => { setDisplayCount(20); }, [activeTab]);

  // IntersectionObserver for infinite scroll (desktop)
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting) setDisplayCount(c => c + 20); },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, displayCount]);

  const handleCellClick = async (eventId, driverId) => {
    // Only the driver themselves can change their own status
    if (!isDriver || driverId !== myDriverId) return;

    const current = registrations.find(r => r.event_id === eventId && r.driver_id === driverId)?.status ?? null;
    // Normalize assigned → participating for cycle purposes
    const normalized = (current === 'assigned') ? 'participating' : current;
    const idx = DRIVER_STATUS_CYCLE.indexOf(normalized);
    const next = DRIVER_STATUS_CYCLE[(idx + 1) % DRIVER_STATUS_CYCLE.length];

    setSaving(true);
    try {
      await setStatus(eventId, driverId, next);
      // Show calendar prompt when signing up
      if (next === 'participating') {
        const ev = events.find(e => e.id === eventId);
        if (ev) setCalPromptEvent(ev);
      }
    }
    catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const getStatus = (eventId, driverId) =>
    registrations.find(r => r.event_id === eventId && r.driver_id === driverId)?.status ?? null;

  const today = new Date(); today.setHours(23, 59, 59, 999);
  const upcomingEvents = events.filter(e => new Date(e.date) >= new Date(new Date().setHours(0,0,0,0)));
  const pastEvents     = events.filter(e => new Date(e.date) < new Date(new Date().setHours(0,0,0,0)));
  const visibleEvents  = activeTab === 'upcoming' ? upcomingEvents : [...pastEvents].reverse();

  // Stats
  const totalEvents = events.length;
  const totalDrivers = drivers.length;
  const totalRegistrations = registrations.filter(r => r.status === 'participating' || r.status === 'assigned').length;
  const participationRate = totalEvents && totalDrivers
    ? Math.round((totalRegistrations / (totalEvents * totalDrivers)) * 1000) / 10
    : 0;

  const allCategories = ['ALL', ...Array.from(new Set(drivers.map(d => d.category || 'Elite'))).sort()];

  const filteredDrivers = categoryFilter === 'ALL'
    ? drivers
    : drivers.filter(d => (d.category || 'Elite') === categoryFilter);

  const fmtDate = (str) => new Date(str)
    .toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: 'short' })
    .toUpperCase();

  const handleNavigateToEvent = (id) => navigate(`/events/${id}`);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: '0.2em', color: 'var(--primary)', animation: 'rmPulse 1.4s infinite' }}>
        LOADING MATRIX…
      </div>
    </div>
  );

  if (error) return (
    <div style={{ padding: '2rem' }}>
      <div style={{ padding: '1rem', background: 'rgba(147,0,10,0.15)', border: '1px solid rgba(255,180,171,0.3)', color: 'var(--error)', fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>
        {error}
        <button onClick={load} style={{ marginLeft: 16, background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, fontWeight: 700 }}>RETRY ↺</button>
      </div>
    </div>
  );

  const myDriver = drivers.find(d => d.id === myDriverId);
  const myCategory = myDriver?.category || null;

  return (
    <>
      {calPromptEvent && (
        <CalendarPrompt
          ev={calPromptEvent}
          driverCategory={myCategory}
          onDismiss={() => setCalPromptEvent(null)}
        />
      )}
      <style>{`
        @keyframes rmPulse { 0%,100%{opacity:1} 50%{opacity:0.4} }

        @media (max-width: 767px) {
          .rm-mobile  { display: block !important; }
          .rm-desktop { display: none  !important; }
        }
        @media (min-width: 768px) {
          .rm-mobile  { display: none  !important; }
          .rm-desktop { display: block !important; }
        }
      `}</style>

      {/* ════════ MOBILE ════════ */}
      <div className="rm-mobile" style={{ display: 'none' }}>
        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontStyle: 'italic', fontSize: 28, letterSpacing: '-0.01em', color: 'var(--on-surface)', margin: '0 0 6px 0', lineHeight: 1.1, textTransform: 'uppercase' }}>
            RACE PARTICIPATION
          </h1>
          <div style={{ height: 3, width: 48, background: 'var(--brand-primary)', marginBottom: 0 }} />
        </div>

        {/* Stat cards — 2 columns */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 24 }}>
          <div style={{ background: 'rgba(30,32,36,0.6)', backdropFilter: 'blur(20px)', border: '1px solid rgba(var(--brand-primary-rgb),0.12)', padding: '14px 16px' }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--outline)', marginBottom: 8 }}>UPCOMING RACES</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 32, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--brand-primary)', lineHeight: 1 }}>{upcomingEvents.length}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontStyle: 'italic', color: 'var(--outline)', marginBottom: 4 }}>TOTAL</span>
            </div>
          </div>
          <div style={{ background: 'rgba(30,32,36,0.6)', backdropFilter: 'blur(20px)', border: '1px solid rgba(var(--brand-primary-rgb),0.12)', borderLeft: '2px solid rgba(var(--brand-primary-rgb),0.4)', padding: '14px 16px' }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--outline)', marginBottom: 8 }}>PARTICIPATION</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 32, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--brand-primary)', lineHeight: 1 }}>{participationRate}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 18, fontWeight: 700, color: 'var(--brand-primary)', marginBottom: 2 }}>%</span>
            </div>
          </div>
        </div>

        {/* ICH / TEAM tab switcher — drivers only */}
        {isDriver && (
          <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid rgba(59,73,75,0.3)' }}>
            {[['ich', 'ICH'], ['team', 'TEAM']].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setMobileView(key)}
                style={{
                  flex: 1, padding: '11px 0',
                  fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: '0.15em',
                  background: 'transparent', border: 'none',
                  borderBottom: mobileView === key ? '2px solid var(--brand-primary)' : '2px solid transparent',
                  color: mobileView === key ? 'var(--brand-primary)' : 'rgba(185,202,203,0.5)',
                  cursor: 'pointer', transition: 'all 0.15s', marginBottom: -1,
                }}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Upcoming / Past sub-tabs */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid rgba(59,73,75,0.3)' }}>
          {[['upcoming', `${t('matrix_upcoming').toUpperCase()} (${upcomingEvents.length})`], ['past', `${t('matrix_past').toUpperCase()} (${pastEvents.length})`]].map(([tab, label]) => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: '9px 16px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', background: 'transparent', border: 'none', borderBottom: activeTab === tab ? '2px solid var(--brand-primary)' : '2px solid transparent', color: activeTab === tab ? 'var(--brand-primary)' : 'rgba(185,202,203,0.5)', cursor: 'pointer', transition: 'all 0.15s', marginBottom: -1 }}>
              {label}
            </button>
          ))}
        </div>

        {/* ── ICH view: flat personal event list ── */}
        {mobileView === 'ich' && isDriver && (
          <>
            {visibleEvents.length === 0 ? (
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'var(--outline)', letterSpacing: '0.15em', padding: '2rem 0', textAlign: 'center' }}>
                {t('matrix_no_events').toUpperCase()}
              </div>
            ) : (
              visibleEvents.map(ev => (
                <MobileMyEventRow
                  key={ev.id}
                  ev={ev}
                  myStatus={getStatus(ev.id, myDriverId)}
                  onToggle={() => handleCellClick(ev.id, myDriverId)}
                  onNavigate={handleNavigateToEvent}
                  saving={saving}
                  fmtDate={fmtDate}
                />
              ))
            )}
            {saving && (
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--brand-primary)', letterSpacing: '0.1em', textAlign: 'center', marginTop: 8, animation: 'rmPulse 1s infinite' }}>SAVING…</div>
            )}
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: 'rgba(132,148,149,0.5)', letterSpacing: '0.08em', textAlign: 'center', marginTop: 12 }}>
              ↕ STATUS TIPPEN UM ZU ÄNDERN
            </div>
          </>
        )}

        {/* ── TEAM view: category filter + accordion ── */}
        {(mobileView === 'team' || !isDriver) && (
          <>
            {/* Category filter */}
            {allCategories.length > 2 && (
              <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
                {allCategories.map(c => (
                  <button
                    key={c}
                    onClick={() => setCategoryFilter(c)}
                    style={{
                      fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.1em',
                      padding: '5px 12px', cursor: 'pointer', border: '1px solid',
                      borderColor: categoryFilter === c ? 'var(--brand-primary)' : 'rgba(59,73,75,0.5)',
                      background: categoryFilter === c ? 'rgba(var(--brand-primary-rgb),0.12)' : 'transparent',
                      color: categoryFilter === c ? 'var(--brand-primary)' : 'var(--outline)',
                      transition: 'all 0.15s',
                    }}
                  >
                    {c}
                  </button>
                ))}
                {saving && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--brand-primary)', letterSpacing: '0.1em', alignSelf: 'center', animation: 'rmPulse 1s infinite' }}>SAVING…</span>}
              </div>
            )}

            {/* Event accordion list */}
            {visibleEvents.length === 0 ? (
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'var(--outline)', letterSpacing: '0.15em', padding: '2rem 0', textAlign: 'center' }}>
                {t('matrix_no_events').toUpperCase()}
              </div>
            ) : (
              <div>
                {visibleEvents.map(ev => (
                  <MobileEventCard
                    key={ev.id}
                    ev={ev}
                    drivers={drivers}
                    getStatus={getStatus}
                    handleCellClick={handleCellClick}
                    isAdmin={isAdminOrCoAdmin}
                    isDriver={isDriver}
                    myDriverId={myDriverId}
                    saving={saving}
                    fmtDate={fmtDate}
                    categoryFilter={categoryFilter}
                    onNavigate={handleNavigateToEvent}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* ════════ DESKTOP (unchanged) ════════ */}
      <div className="rm-desktop" style={{ display: 'block', padding: '2rem', maxWidth: '100%' }}>

        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.2em', color: 'var(--primary)', marginBottom: 8, textTransform: 'uppercase' }}>
            RACE PARTICIPATION MATRIX
          </div>
          <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontStyle: 'italic', fontSize: 'clamp(1.5rem, 4vw, 2.5rem)', letterSpacing: '0.04em', color: 'var(--on-surface)', margin: 0, lineHeight: 1.1 }}>
            {t('matrix_title')}
          </h1>
          {/* Desktop tabs */}
          <div style={{ display: 'flex', gap: 0, marginTop: 20, borderBottom: '1px solid rgba(59,73,75,0.3)' }}>
            {[['upcoming', `${t('matrix_upcoming').toUpperCase()} (${upcomingEvents.length})`], ['past', `${t('matrix_past').toUpperCase()} (${pastEvents.length})`]].map(([tab, label]) => (
              <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: '9px 20px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', background: 'transparent', border: 'none', borderBottom: activeTab === tab ? '2px solid var(--brand-primary)' : '2px solid transparent', color: activeTab === tab ? 'var(--brand-primary)' : 'rgba(185,202,203,0.5)', cursor: 'pointer', transition: 'all 0.15s', marginBottom: -1 }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Stat cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1px', background: 'var(--outline-variant)', border: '1px solid var(--outline-variant)', marginBottom: '2rem' }}>
          {[
            { label: t('matrix_upcoming_races').toUpperCase(),  value: upcomingEvents.length,   sub: t('general_total') },
            { label: t('matrix_rider').toUpperCase(),          value: totalDrivers,            sub: t('matrix_in_squad').toUpperCase() },
            { label: t('matrix_registrations').toUpperCase(),  value: totalRegistrations,      sub: t('matrix_confirmed').toUpperCase() },
            { label: t('matrix_rate').toUpperCase(),           value: `${participationRate}%`, sub: 'PARTICIPATION RATE', highlight: participationRate >= 70 },
          ].map(({ label, value, sub, highlight }) => (
            <div key={label} style={{ background: 'var(--surface-container)', padding: '1.25rem 1.5rem' }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', textTransform: 'uppercase', marginBottom: 8 }}>{label}</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', color: highlight ? 'var(--brand-secondary)' : 'var(--on-surface)', lineHeight: 1 }}>{value}</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--outline)', letterSpacing: '0.1em', marginTop: 6 }}>{sub}</div>
            </div>
          ))}
        </div>

        {/* Matrix panel */}
        <div style={{ background: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
          {/* Panel header */}
          <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--outline-variant)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 700, fontStyle: 'italic', color: 'var(--on-surface)', letterSpacing: '0.05em' }}>
              RIDER ASSIGNMENT GRID
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              {/* Legend */}
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                {[
                  ['participating', t('events_registered')],
                  ['not_participating', t('evdetail_not_reg')],
                  ['none', t('general_none')],
                ].map(([key, label]) => {
                  const meta = STATUS_META[key];
                  return (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <div style={{ width: 12, height: 12, background: meta.bg, border: `1px solid ${meta.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, color: meta.color, fontWeight: 700 }}>
                        {meta.icon}
                      </div>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--outline)', textTransform: 'uppercase' }}>{label}</span>
                    </div>
                  );
                })}
              </div>
              {/* Category filter */}
              <select
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value)}
                style={{ background: 'var(--surface-container-high)', border: '1px solid var(--outline-variant)', color: 'var(--on-surface)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', padding: '4px 8px', cursor: 'pointer', outline: 'none' }}
              >
                {allCategories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {saving && (
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--primary)', letterSpacing: '0.1em', animation: 'rmPulse 1s infinite' }}>
                  SAVING…
                </div>
              )}
            </div>
          </div>

          {/* Table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '180px' }} />
                {filteredDrivers.map(d => <col key={d.id} style={{ width: '52px' }} />)}
              </colgroup>
              <thead>
                <tr style={{ position: 'sticky', top: 0, zIndex: 10, background: 'var(--surface-container-high)' }}>
                  <th style={{ padding: '10px 16px', textAlign: 'left', borderRight: '1px solid var(--outline-variant)', borderBottom: '1px solid var(--outline-variant)', position: 'sticky', left: 0, background: 'var(--surface-container-high)', zIndex: 11 }}>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', textTransform: 'uppercase' }}>EVENT / {t('evdetail_field_date').toUpperCase()}</span>
                  </th>
                  {filteredDrivers.map(driver => {
                    const isMyCol = isDriver && driver.id === myDriverId;
                    const initials = (driver.name || '?').split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
                    return (
                      <th key={driver.id} style={{ padding: '8px 4px', textAlign: 'center', borderRight: '1px solid rgba(59,73,75,0.3)', borderBottom: '1px solid var(--outline-variant)', background: isMyCol ? 'rgba(var(--brand-primary-rgb),0.06)' : undefined }}>
                        <div title={driver.name} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                          {isMyCol && <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 7, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--brand-primary)' }}>ICH</div>}
                          {driver.photo_url ? (
                            <img src={driver.photo_url} alt={initials} style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: `1px solid ${isMyCol ? 'rgba(var(--brand-primary-rgb),0.5)' : 'rgba(59,73,75,0.4)'}` }} />
                          ) : (
                            <div style={{ width: 32, height: 32, borderRadius: '50%', background: isMyCol ? 'rgba(var(--brand-primary-rgb),0.12)' : 'rgba(51,53,57,0.8)', border: `1px solid ${isMyCol ? 'rgba(var(--brand-primary-rgb),0.4)' : 'rgba(59,73,75,0.4)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, color: isMyCol ? 'var(--brand-primary)' : 'var(--on-surface-variant)' }}>{initials}</span>
                            </div>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {visibleEvents.length === 0 && (
                  <tr>
                    <td colSpan={filteredDrivers.length + 1} style={{ padding: '3rem', textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'var(--outline)', letterSpacing: '0.15em' }}>
                      {t('matrix_no_events_found').toUpperCase()}
                    </td>
                  </tr>
                )}
                {visibleEvents.slice(0, displayCount).map((ev, rowIdx) => {
                  const evCancelled = !!ev.is_cancelled;
                  const rowBg = evCancelled ? 'rgba(24,6,6,0.9)' : ev.is_mandatory ? 'rgba(22,12,2,0.85)' : (rowIdx % 2 === 0 ? 'var(--surface-container)' : 'var(--surface-container-low)');
                  return (
                    <tr
                      key={ev.id}
                      onClick={() => handleNavigateToEvent(ev.id)}
                      style={{ background: rowBg, borderLeft: evCancelled ? '3px solid rgba(220,50,50,0.5)' : ev.is_mandatory ? '3px solid rgba(255,160,50,0.5)' : 'none', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = evCancelled ? 'rgba(40,8,8,0.95)' : ev.is_mandatory ? 'rgba(35,20,4,0.9)' : 'var(--surface-container-high)'}
                      onMouseLeave={e => e.currentTarget.style.background = rowBg}
                    >
                      <td style={{ padding: '12px 16px', borderRight: '1px solid var(--outline-variant)', borderBottom: '1px solid rgba(59,73,75,0.2)', position: 'sticky', left: 0, background: 'inherit', zIndex: 2 }}>
                        {(evCancelled || ev.is_mandatory) && (
                          <div style={{ marginBottom: 3, display: 'flex', gap: 5 }}>
                            {evCancelled && (
                              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', background: '#dc2626', color: '#fff', padding: '1px 5px' }}>✕ ABGESAGT</span>
                            )}
                            {ev.is_mandatory && !evCancelled && (
                              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', background: '#ff9a1f', color: '#1a0800', padding: '1px 5px' }}>PFLICHT</span>
                            )}
                          </div>
                        )}
                        <div style={{ fontFamily: "var(--font-display)", fontSize: 12, fontWeight: 800, fontStyle: 'italic', color: evCancelled ? 'rgba(220,100,100,0.7)' : ev.is_mandatory ? '#ffb86c' : 'var(--primary)', letterSpacing: '0.04em', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: evCancelled ? 'line-through' : 'none' }} title={ev.name}>
                          {ev.name.toUpperCase()}
                        </div>
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--outline)', letterSpacing: '0.1em', display: 'flex', gap: 8 }}>
                          <span>{fmtDate(ev.date)}</span>
                          {ev.location && <span>· {ev.location.toUpperCase()}</span>}
                        </div>
                      </td>
                      {filteredDrivers.map(driver => {
                        const isMyCol = isDriver && driver.id === myDriverId;
                        const canEdit = isMyCol;
                        return (
                          <td
                            key={driver.id}
                            onClick={canEdit ? e => { e.stopPropagation(); handleCellClick(ev.id, driver.id); } : undefined}
                            style={{ padding: '8px', textAlign: 'center', borderRight: '1px solid rgba(59,73,75,0.15)', borderBottom: '1px solid rgba(59,73,75,0.2)', background: isMyCol ? 'rgba(var(--brand-primary-rgb),0.04)' : undefined, cursor: canEdit ? 'pointer' : 'inherit' }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                              <StatusCell
                                status={getStatus(ev.id, driver.id)}
                                onClick={() => {}}
                                canEdit={canEdit}
                              />
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Sentinel for infinite scroll */}
          {displayCount < visibleEvents.length && (
            <div ref={sentinelRef} style={{ padding: '16px', textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--outline)', letterSpacing: '0.12em', animation: 'rmPulse 1.4s infinite' }}>
              LOADING…
            </div>
          )}

          {isDriver && (
            <div style={{ padding: '10px 16px', borderTop: '1px solid var(--outline-variant)', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--outline)', letterSpacing: '0.1em' }}>
              DEINE SPALTE: Zelle klicken → Angemeldet → Abgemeldet → Keine Anmeldung
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default RaceMatrix;
